import { useGeoStore } from '@/stores/geo';
import { useLocationStore } from '@/stores/location';
import { useMrxStore } from '@/stores/mrx';
import { useTrackStore } from '@/stores/track';
import { useDangerSignStore } from '@/features/danger-signs/stores/dangerSign.store';
import { useGuidedModeStore } from '@/features/guided-mode/stores/guidedMode';
import { useNoteStore } from '@/features/text-mode/stores/note.store';
import { useQuestionStore } from '@/features/text-mode/stores/question.store';
import { buildAssessmentPayload, buildUnaskedQuestionNames, UNANSWERED_SENTINEL } from '@/features/guided-mode/utils/assessmentPayload';
import { withDangerSignScreen } from '@/features/guided-mode/utils/danger-sign-screen';
import { guidedObservationName } from '@/features/guided-mode/utils/mrx-observations';
import { getDangerSignQuestionOrder } from '@shared/assessment-definition/dist/configurations/danger-signs';
import { buildEncounterDetails } from '@/utils/encounterDetails';
import { gatherResponses } from '@/utils/panelResponses';
import type { AttributeValues } from '@shared/assessment-definition/dist/src/attributes';
import type {
  GuidedAnswer,
  GuidedModeDefinition,
} from '@/features/guided-mode/types/guided-mode.types';

export interface CollectPayloadOptions {
  deIdentify?: boolean;
  clinicalNoteText?: string | null;
  encounterId?: string | null;
}

export interface CollectPayloadResponse {
  name: string;
  value: string | null;
  panel?: string;
  token: string;
  panel_type?: string;
  panel_input?: string;
  attributes?: AttributeValues;
}

function deIdentifyPatient(patient: Record<string, any>, identifyingPanels: string[]) {
    const fields: Record<string, string> = {
        age: 'age',
        ageInDays: 'age',
        gender: 'gender',
        name: 'patient_name',
        patientId: 'patient_id',
        phoneNumber: 'patient_phone',
        birthDate: 'age'
    };

    return Object.fromEntries(
        Object.entries(patient).map(([key, value]) => [
            key,
            identifyingPanels.includes(fields[key]) ? undefined : value
        ])
    );
}

// an absent or blank note is omitted from the payload, not sent as empty
function mapClinicalNoteText(clinicalNoteText?: string | null): string | undefined {
    if (clinicalNoteText == null) {
        return undefined;
    }

    const trimmedText = String(clinicalNoteText).trim();
    if (!trimmedText) {
        return undefined;
    }

    return trimmedText;
}

// an unknown track must not read as "nothing is identifying": that uploads names un-redacted
export function identifyingPanels(): string[] {
    const trackStore = useTrackStore();
    const details = buildEncounterDetails();
    const track = trackStore.all.find((t: any) => t.id === details.track.id) || trackStore.current;
    if (!Array.isArray(track?.panels)) return [];
    return track.panels
        .filter((panel: any) => panel.options && panel.options.identifying === true)
        .map((panel: any) => panel.name);
}

function dangerSignUnaskedQuestionNames(
    definition: GuidedModeDefinition | null,
    answers: Record<string, GuidedAnswer>,
    dangerSignCodes: readonly string[],
    excludedDataPointCodes: readonly string[],
): string[] {
    if (!definition || !dangerSignCodes.length) return [];

    const entries = buildAssessmentPayload(definition, answers, { excludedDataPointCodes });
    const entryByName = new Map(entries.map(entry => [entry.name, entry]));

    return dangerSignCodes.filter(code => {
        const entry = entryByName.get(code);
        // A danger sign the definition does not carry was never offered at all, and nothing can
        // have hidden it.
        if (!entry) return true;
        return entry.value === UNANSWERED_SENTINEL && !entry.isSkip;
    });
}

function guidedUnaskedQuestionNames(): string[] {
    const guidedModeStore = useGuidedModeStore();
    const trackStore = useTrackStore();
    const path = guidedModeStore.activeAssessmentPath;
    const baseDefinition = path
        ? guidedModeStore.clinicalContent.assessmentDefinitions?.[path]
        : undefined;
    if (!baseDefinition) return [];

    const dangerSignCodes = trackStore.dangerSignsEnabled && path
        ? getDangerSignQuestionOrder(path)
        : [];
    const definition = withDangerSignScreen(baseDefinition, dangerSignCodes);

    const focusedPanelNames = trackStore.initialAssessmentType === 'focused' && !trackStore.comprehensive
        ? guidedModeStore.focusedPanelNames
        : undefined;

    const excludedDataPointCodes = guidedModeStore.clinicalContent.questionEligibility?.[path!] || [];

    const unaskedQuestionNames = buildUnaskedQuestionNames(definition, guidedModeStore.effectiveAnswers, {
        excludedDataPointCodes,
        focusedPanelNames,
    });

    const unaskedDangerSignNames = dangerSignUnaskedQuestionNames(
        definition,
        guidedModeStore.effectiveAnswers,
        dangerSignCodes,
        excludedDataPointCodes,
    );

    return Array.from(new Set([...unaskedQuestionNames, ...unaskedDangerSignNames]));
}

function attributesByName(): Map<string, AttributeValues | undefined> {
    const guidedModeStore = useGuidedModeStore();
    const path = guidedModeStore.activeAssessmentPath;
    const definition = path
        ? guidedModeStore.clinicalContent.assessmentDefinitions?.[path] : undefined;
    const byName = new Map<string, AttributeValues | undefined>();
    if (definition) {
        const answerAttributes = guidedModeStore.answerAttributes || {};
        Object.entries(answerAttributes).forEach(([conceptId, attributes]) => {
            byName.set(guidedObservationName(definition, conceptId), attributes);
        });
    }
    // MRX reads the note's response for a name both modes hold, so the note's attributes win.
    const noteNames = new Set<string>();
    useNoteStore().originalObservations.forEach(observation => {
        if (noteNames.has(observation.name)) return;
        noteNames.add(observation.name);
        byName.set(observation.name, observation.attributes);
    });
    return byName;
}

export async function buildCollectPayload(
    { deIdentify = true, clinicalNoteText, encounterId }: CollectPayloadOptions = {},
): Promise<Record<string, any>> {
    var results: Record<string, any> = {}

    const locationStore = useLocationStore();
    const locations = locationStore.responses;
    const bundle = useMrxStore().bundleSnapshot;
    let responses = await gatherResponses(useTrackStore().current)
    let details = buildEncounterDetails()

    // A stopped encounter records the questions it never reached, so the record shows what the
    // worker was never asked rather than what they answered.
    const stopped = useDangerSignStore().stopRequested;
    if (stopped) {
        const answered = new Set(responses.map((c: any) => c.name));
        // The two modes can name the same question, and an answered entry always wins.
        const unasked = [...new Set([
            ...guidedUnaskedQuestionNames(),
            ...useQuestionStore().unaskedQuestionNames,
        ])];
        // A new array, so the gathered responses are never mutated in place.
        const notAsked = unasked
            .filter(name => !answered.has(name))
            .map(name => ({
                name,
                value: UNANSWERED_SENTINEL,
                panel_name: undefined,
                datapoint: undefined,
                panel_input: undefined,
            }));
        responses = [...responses, ...notAsked];
    }

    let assessment = bundle.filter((item: any) => (item.type === 'assessment'))
    let recommendations = bundle.filter((item: any) => (item.type === 'plan'))
    let medicationRequests = bundle.filter((item: any) => (item.type === 'plan' && item.category === 'medication' && item.shown === true))
    let panels = identifyingPanels();

    const attributes = attributesByName();
    let mappedResponses: CollectPayloadResponse[] = responses.map((c: any) => {
        const responseAttributes = c.value === UNANSWERED_SENTINEL
            ? undefined : attributes.get(c.name);
        return {
            name: c.name,
            value: (deIdentify && panels.includes(c.name)) ? null : String(c.value),
            panel: c.panel_name,
            token: c.name === c.panel_name ? `questions.${c.name}.title` : `responses.${c.name}.title`,
            panel_type: c.datapoint,
            panel_input: c.panel_input,
            ...(responseAttributes && { attributes: { ...responseAttributes } }),
        }
    })

    let mappedLocations: any[] = [];
    if (locations && Object.keys(locations).length > 0) {
        mappedLocations = Object.values(locations).map((c: any) => {
            return {
                name: c.name,
                token: c.token,
                value: c.value,
                location_type: c.location_type
            }
        })
    }

    let mappedAssessments = assessment.map((c: any) => {
        return {
            name: c.name,
            severity: c.severity,
            score: c.score,
            overridden: c.overridden || null,
            selected: c.selected || false,
            triage: c.triage_recommendation
        }
    })

    results = Object.assign({}, details);

    results.id = encounterId || results.id.id;
    results.engagement_code = results.encounter.code;
    results.completed_date = results.timing.end;
    results.responses = mappedResponses;
    results.locations = mappedLocations;
    results.assessments = mappedAssessments;

    const mappedClinicalNoteText = mapClinicalNoteText(clinicalNoteText);
    if (mappedClinicalNoteText) {
        results.clinical_note_text = mappedClinicalNoteText;
    }

    if (deIdentify) {
        results.patient = deIdentifyPatient(results.patient, panels);
    }

    if (recommendations.length > 0) {
        results.recommendations = recommendations.map((rec: any) => {
            return {
                category: rec.category,
                name: rec.name,
                assessment: rec.assessment,
                severity: rec.severity,
                shown: rec.shown || false,
            }
        })
    }

    if (medicationRequests.length > 0) {
        // 1. Create a temporary object to hold our grouped medications.
        const groupedMeds: Record<string, any> = {};

        // 2. Loop through each medication request one by one.
        medicationRequests.forEach((medRequest: any) => {
            const name = medRequest.name;

            // 3. If we haven't seen this medication before, create its entry.
            if (!groupedMeds[name]) {
                // Find the corresponding treatment info
                const treatmentBundle: Record<string, any> =
                    bundle.find((item: any) => item.type === 'treatment' && item.name === name) || {};

                // get extended entries
                const filterKeys = ['assessment', 'delivery', 'group', 'name', 'order', 'priority', 'severity', 'treatmentFlags', 'type']
                const extended = Object.entries(treatmentBundle)
                    .filter(([key]) => !filterKeys.includes(key))
                    .map(([key, value]) => ({
                        name: key,
                        value: String(value),
                    }))

                groupedMeds[name] = {
                    name: name,
                    assessments: [], // Start with an empty list of assessments
                    shown: medRequest.shown || false,
                    selected: medRequest.selected || false,
                    group: treatmentBundle?.group || null,
                    priority: treatmentBundle?.priority || null,
                    order: treatmentBundle?.order || null,
                    extended,
                };
            }

            // 4. Add the current assessment details to its group.
            groupedMeds[name].assessments.push({
                assessment: medRequest.assessment,
                severity: medRequest.severity
            });
        });

        // 5. Convert the object of grouped medications back into an array.
        results.medicationRequests = Object.values(groupedMeds);
    }

    const geoStore = useGeoStore();
    results.geo = geoStore.$state;

    results.etl = {
        version: '6.0'
    }

    results.dataContext = `flexi-${details.encounter.env}`

    return results
}
